import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import companyApi from '../../../api/company/CompanyApi'
import {
    CompanyCsSearchMode,
    CompanyCsSearchResultDto,
    CompanyEntityFilter,
} from '../../../api/company/models/companyCs/CompanyCsDtos'
import Dropdown, {
    Item as DropdownItem,
} from '../../common/form/inputs/DropDown'
import Button from '../../common/form/inputs/Button'
import LoadingSpinner from '../../common/loading/LoadingSpinner'
import LinkedInLink from '../../../components/LinkedInLink'

const DEFAULT_DEBOUNCE_MS = 300
const COMPANY_SEARCH_PARAM = 'companyColdStorageSearch'
const COMPANY_ENTITY_FILTER_PARAM = 'companyEntityFilter'
const COMPANY_SEARCH_MODE_PARAM = 'companySearchMode'

const DEFAULT_SEARCH_MODE: CompanyCsSearchMode = 'name'

type SearchModeOption = {
    value: CompanyCsSearchMode
    label: string
}

const DEFAULT_SEARCH_MODE_OPTIONS: readonly SearchModeOption[] = [
    { value: 'id', label: 'Company ID' },
    { value: 'name', label: 'Name' },
] as const

const COMPANY_ENTITY_ITEMS: DropdownItem[] = [
    { id: 'all', content: 'All' },
    { id: 'hasDataProviderId', content: 'Has Data Provider Id' },
    { id: 'noDataProviderId', content: 'No Data Provider Id' },
]

const parseCompanySearchMode = (value: string | null): CompanyCsSearchMode => {
    return value === 'id' ? 'id' : 'name'
}

const parseCompanyEntityFilter = (
    value: string | null,
): CompanyEntityFilter => {
    if (value === 'hasDataProviderId') return 'hasDataProviderId'
    if (value === 'noDataProviderId') return 'noDataProviderId'
    return 'all'
}

type CsCompanySearchProps = {
    allowEmptyQuery?: boolean
    minQueryLength?: number
    searchDebounceMs?: number
    enableUrlSync?: boolean
    enforcedCompanyEntityFilter?: Extract<
        CompanyEntityFilter,
        'hasDataProviderId' | 'noDataProviderId'
    >
    onSelect?: (company: CompanyCsSearchResultDto) => void
    selectedCompanyId?: string
    selectedCompanyIds?: ReadonlyArray<string>
    multiSelect?: boolean
    searchPlaceholder?: string
    showFilterDropdown?: boolean
    searchModeOptions?: readonly SearchModeOption[]
    tableWrapperClassName?: string
    emptyStateMessage?: string
    showLogoColumn?: boolean
}

export default function CsCompanySearch({
    allowEmptyQuery = true,
    minQueryLength = 0,
    searchDebounceMs = DEFAULT_DEBOUNCE_MS,
    enableUrlSync = false,
    enforcedCompanyEntityFilter,
    onSelect,
    selectedCompanyId,
    selectedCompanyIds,
    multiSelect = false,
    searchPlaceholder = 'Search by company ID or company name',
    showFilterDropdown = true,
    searchModeOptions = DEFAULT_SEARCH_MODE_OPTIONS,
    tableWrapperClassName,
    emptyStateMessage = 'No results',
    showLogoColumn = false,
}: CsCompanySearchProps): React.JSX.Element {
    const [searchParams, setSearchParams] = useSearchParams()
    const initialQuery = enableUrlSync
        ? searchParams.get(COMPANY_SEARCH_PARAM) || ''
        : ''
    const [query, setQuery] = useState(initialQuery)
    const [results, setResults] = useState<CompanyCsSearchResultDto[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | undefined>()
    const [pageNumber, setPageNumber] = useState(1)
    const [perPage, setPerPage] = useState(100)
    const [hasMore, setHasMore] = useState(false)
    const [companyEntityFilter, setCompanyEntityFilter] =
        useState<CompanyEntityFilter>(() => {
            if (enforcedCompanyEntityFilter) return enforcedCompanyEntityFilter
            return enableUrlSync
                ? parseCompanyEntityFilter(
                      searchParams.get(COMPANY_ENTITY_FILTER_PARAM),
                  )
                : 'all'
        })
    const [searchMode, setSearchMode] = useState<CompanyCsSearchMode>(() => {
        if (!enableUrlSync) return DEFAULT_SEARCH_MODE
        return parseCompanySearchMode(
            searchParams.get(COMPANY_SEARCH_MODE_PARAM),
        )
    })

    const trimmedQuery = useMemo(() => query.trim(), [query])
    const meetsQueryThreshold = trimmedQuery.length >= minQueryLength
    const canSearch = allowEmptyQuery || meetsQueryThreshold

    const selectedIdsSet = useMemo(() => {
        if (selectedCompanyIds && selectedCompanyIds.length > 0) {
            return new Set(selectedCompanyIds)
        }
        if (selectedCompanyId) return new Set([selectedCompanyId])
        return new Set<string>()
    }, [selectedCompanyId, selectedCompanyIds])

    const availableFilterItems = useMemo(() => {
        if (!enforcedCompanyEntityFilter) return COMPANY_ENTITY_ITEMS
        return COMPANY_ENTITY_ITEMS.filter(
            (item) => item.id === enforcedCompanyEntityFilter,
        )
    }, [enforcedCompanyEntityFilter])

    const companyEntityDropdownItem = useMemo(() => {
        return (
            availableFilterItems.find(
                (item) => item.id === companyEntityFilter,
            ) || availableFilterItems[0]
        )
    }, [availableFilterItems, companyEntityFilter])

    useEffect(() => {
        if (!enableUrlSync) return
        const urlQuery = searchParams.get(COMPANY_SEARCH_PARAM) || ''
        setQuery((prev) => (prev !== urlQuery ? urlQuery : prev))
        const urlFilter = parseCompanyEntityFilter(
            searchParams.get(COMPANY_ENTITY_FILTER_PARAM),
        )
        setCompanyEntityFilter((prev) =>
            enforcedCompanyEntityFilter
                ? enforcedCompanyEntityFilter
                : prev !== urlFilter
                  ? urlFilter
                  : prev,
        )
        setSearchMode((prev) =>
            prev !==
            parseCompanySearchMode(searchParams.get(COMPANY_SEARCH_MODE_PARAM))
                ? parseCompanySearchMode(
                      searchParams.get(COMPANY_SEARCH_MODE_PARAM),
                  )
                : prev,
        )
    }, [searchParams, enableUrlSync, enforcedCompanyEntityFilter])

    useEffect(() => {
        if (!enableUrlSync) return
        const current = searchParams.toString()
        const next = new URLSearchParams(searchParams)
        if (query) {
            next.set(COMPANY_SEARCH_PARAM, query)
        } else {
            next.delete(COMPANY_SEARCH_PARAM)
        }
        if (companyEntityFilter && companyEntityFilter !== 'all') {
            next.set(COMPANY_ENTITY_FILTER_PARAM, companyEntityFilter)
        } else {
            next.delete(COMPANY_ENTITY_FILTER_PARAM)
        }
        if (searchMode !== DEFAULT_SEARCH_MODE) {
            next.set(COMPANY_SEARCH_MODE_PARAM, searchMode)
        } else {
            next.delete(COMPANY_SEARCH_MODE_PARAM)
        }
        if (next.toString() !== current) {
            setSearchParams(next, { replace: true })
        }
    }, [
        query,
        companyEntityFilter,
        searchMode,
        enableUrlSync,
        searchParams,
        setSearchParams,
    ])

    useEffect(() => {
        setPageNumber(1)
    }, [trimmedQuery, companyEntityFilter, searchMode])

    useEffect(() => {
        if (!enforcedCompanyEntityFilter) return
        setCompanyEntityFilter(enforcedCompanyEntityFilter)
    }, [enforcedCompanyEntityFilter])

    useEffect(() => {
        if (!canSearch) {
            setResults([])
            setHasMore(false)
            setError(undefined)
            return
        }

        let ignore = false
        const handle = window.setTimeout(async () => {
            try {
                setLoading(true)
                const res = await companyApi.searchCompanyCs({
                    q: trimmedQuery || undefined,
                    pageNumber,
                    perPage,
                    companyEntityFilter,
                    searchMode,
                })
                if (ignore) return
                setResults(res.results)
                setPerPage((prev) =>
                    prev !== res.perPage ? res.perPage : prev,
                )
                setHasMore(res.hasMore)
                setError(undefined)
            } catch (e: any) {
                if (!ignore) {
                    setError(e?.message || 'Failed to fetch company records')
                }
            } finally {
                if (!ignore) {
                    setLoading(false)
                }
            }
        }, searchDebounceMs)
        return () => {
            ignore = true
            window.clearTimeout(handle)
        }
    }, [
        trimmedQuery,
        pageNumber,
        perPage,
        companyEntityFilter,
        searchMode,
        canSearch,
        searchDebounceMs,
    ])

    const onPrev = () => {
        setPageNumber((prev) => Math.max(1, prev - 1))
    }

    const onNext = () => {
        setPageNumber((prev) => prev + 1)
    }

    const buildTabSearch = (
        tabSlug: string,
        extraParams: Record<string, string | undefined>,
    ) => {
        const params = new URLSearchParams()
        params.set('tab', tabSlug)
        Object.entries(extraParams).forEach(([key, value]) => {
            if (value) params.set(key, value)
        })
        const search = params.toString()
        return { pathname: '', search: search ? `?${search}` : '' }
    }

    const renderSelectColumn = Boolean(onSelect)
    const showFilter =
        showFilterDropdown &&
        !enforcedCompanyEntityFilter &&
        availableFilterItems.length > 0
    const totalColumnCount =
        (showLogoColumn ? 8 : 7) + (renderSelectColumn ? 1 : 0)

    return (
        <div className="flex flex-col gap-3">
            <input
                className="form-input"
                placeholder={searchPlaceholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
            />
            {searchModeOptions.length > 1 && (
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-700">
                    <span className="font-semibold">Search by:</span>
                    {searchModeOptions.map((option) => (
                        <label
                            key={option.value}
                            className="inline-flex items-center gap-2"
                        >
                            <input
                                type="radio"
                                className="form-radio"
                                name="company-search-mode"
                                value={option.value}
                                checked={searchMode === option.value}
                                onChange={() => setSearchMode(option.value)}
                            />
                            {option.label}
                        </label>
                    ))}
                </div>
            )}
            {showFilter && (
                <div className="w-full max-w-xs">
                    <Dropdown
                        id="company-entity-filter"
                        name="Company Entity"
                        placeholder="Select company entity filter"
                        items={availableFilterItems}
                        selected={companyEntityDropdownItem}
                        onChange={(item) =>
                            setCompanyEntityFilter(
                                parseCompanyEntityFilter(item.id),
                            )
                        }
                    />
                </div>
            )}
            {loading && (
                <div className="py-4">
                    <LoadingSpinner
                        spinnerSize={48}
                        captions={[
                            {
                                caption: trimmedQuery
                                    ? 'Searching companies…'
                                    : 'Loading companies…',
                            },
                        ]}
                        className="h-auto w-full"
                    />
                </div>
            )}
            {error && <div className="text-red-600">{error}</div>}
            <div className={tableWrapperClassName ?? 'overflow-x-auto'}>
                <table className="main-table w-full">
                    <thead>
                        <tr>
                            <th className="text-left">Company ID</th>
                            {showLogoColumn && (
                                <th className="text-left">Logo</th>
                            )}
                            <th className="text-left">Data Provider ID</th>
                            <th className="text-left">LinkedIn</th>
                            <th className="text-left">Name</th>
                            <th className="text-left">Matching Name</th>
                            <th className="text-left">Current Employees</th>
                            <th className="text-left">Work Histories</th>
                            {renderSelectColumn && (
                                <th className="text-left">Select</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {results.map((company) => {
                            const displayName = company.name || '-'
                            const logoUrl = company.companyLogoUrl?.trim()
                            const isSelected = selectedIdsSet.has(company.id)
                            const selectLabel = multiSelect
                                ? isSelected
                                    ? 'Remove'
                                    : 'Add'
                                : isSelected
                                  ? 'Selected'
                                  : 'Select'

                            return (
                                <tr
                                    key={company.id}
                                    className={
                                        isSelected ? 'bg-sky-50' : undefined
                                    }
                                >
                                    <td className="text-xs text-gray-700">
                                        {company.id}
                                    </td>
                                    {showLogoColumn && (
                                        <td className="py-1">
                                            {logoUrl ? (
                                                <img
                                                    src={logoUrl}
                                                    alt={`${displayName} logo`}
                                                    className="h-12 w-12 object-contain"
                                                    loading="lazy"
                                                />
                                            ) : null}
                                        </td>
                                    )}
                                    <td className="text-xs text-gray-700">
                                        {company.dataProviderId || '-'}
                                    </td>
                                    <td>
                                        {company.linkedInUrl ? (
                                            <LinkedInLink
                                                url={company.linkedInUrl}
                                                ariaLabel={`Open LinkedIn page for ${displayName}`}
                                            />
                                        ) : (
                                            '-'
                                        )}
                                    </td>
                                    <td>{displayName}</td>
                                    <td>{company.matchingName || '-'}</td>
                                    <td className="text-right text-sm text-gray-700">
                                        {company.currentEmployeeCount.toLocaleString()}
                                    </td>
                                    <td>
                                        {company.id ? (
                                            <Link
                                                to={buildTabSearch(
                                                    'work-history-cold-storage',
                                                    {
                                                        workHistorySearch:
                                                            company.id,
                                                    },
                                                )}
                                                className="text-blue-600 hover:underline"
                                            >
                                                view
                                            </Link>
                                        ) : (
                                            '-'
                                        )}
                                    </td>
                                    {renderSelectColumn && (
                                        <td>
                                            <Button
                                                type="button"
                                                style="secondary"
                                                onClick={() =>
                                                    onSelect?.(company)
                                                }
                                            >
                                                {selectLabel}
                                            </Button>
                                        </td>
                                    )}
                                </tr>
                            )
                        })}
                        {results.length === 0 && !loading && (
                            <tr>
                                <td
                                    colSpan={totalColumnCount}
                                    className="py-6 text-center"
                                >
                                    {emptyStateMessage}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <div className="flex justify-between items-center mt-2">
                <Button
                    type="button"
                    style="secondary"
                    onClick={onPrev}
                    enabled={pageNumber > 1 && !loading}
                >
                    Previous
                </Button>
                <div className="text-sm text-gray-600">
                    Page {pageNumber} · Showing {results.length} · Per page{' '}
                    {perPage}
                </div>
                <Button
                    type="button"
                    style="secondary"
                    onClick={onNext}
                    enabled={!loading && hasMore}
                >
                    Next
                </Button>
            </div>
        </div>
    )
}

export type { CompanyCsSearchResultDto }
